// src/pages/reports/ProductWiseSalesPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Package, ShoppingCart, DollarSign, Tag, Boxes, Sheet, Download } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import FilterPanel from '../../components/common/FilterPanel/FilterPanel';
import ReportDatePresetFilter from '../../components/common/ReportDatePresetFilter/ReportDatePresetFilter';
import StatsCards from '../../components/common/StatsCards/StatsCards';
import DataTable from '../../components/common/DataTable/DataTable';
import { getProductWiseSalesReport } from '../../services/api/productWiseSalesReport';
import { getShops } from '../../services/api/shops';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/currencyUtil';
import { getCssVar } from '../../utils/themeColors';
import { exportProductWiseSalesExcel, exportProductWiseSalesPDF } from '../../utils/productWiseSalesExport';
import styles from './ProductWiseSalesPage.module.css';

const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const REPORT_CHANNELS = ['online', 'offline', 'pos'];
const CHANNEL_LABEL_KEYS = { online: 'report.channelOnline', offline: 'report.channelOffline', pos: 'report.channelPos' };

const getDefaultFilters = () => {
  const today = toYMD(new Date());
  return { shopIds: [], channels: REPORT_CHANNELS, date: { dateFrom: today, dateTo: today }, search: '' };
};

const EMPTY_SUMMARY = {
  orderCount: 0, orderAmount: 0, orderDiscount: 0,
  productCount: 0, productAmount: 0, productDiscount: 0,
};

const fmtNumber = (v) => (v === null || v === undefined ? '—' : formatCurrency(v));
const fmtPercent = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}%`);

const ProductWiseSalesPage = () => {
  const { t } = useLocalization();
  const { error: showError, warning: showWarning } = useAlert();

  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState(getDefaultFilters);
  const [shops, setShops] = useState([]);
  const [sorting, setSorting] = useState({ orderBy: 'itemCount', orderDirection: 'DESC' });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 25,
    total: 0,
    pageSizeOptions: [25, 50, 100, 200, 500],
  });
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    getShops({ assigned: true, type: 'online,pos' })
      .then((res) => setShops(Array.isArray(res) ? res : res.shops || []))
      .catch((err) => console.error('Failed to load shops:', err));
  }, []);

  const buildParams = () => {
    const params = {
      dateFrom: filters.date.dateFrom,
      dateTo: filters.date.dateTo,
      orderBy: sorting.orderBy,
      orderDirection: sorting.orderDirection,
    };
    if (filters.shopIds.length > 0) params.shopIds = filters.shopIds.join(',');
    if (filters.channels.length < REPORT_CHANNELS.length) params.channels = filters.channels.join(',');
    if (filters.search?.trim()) params.search = filters.search.trim();
    return params;
  };

  const fetchReport = async () => {
    if (!filters.date?.dateFrom || !filters.date?.dateTo) return;
    if (filters.channels.length === 0) {
      showWarning(t('report.selectAtLeastOneChannel'));
      return;
    }
    try {
      setIsLoading(true);
      const data = await getProductWiseSalesReport({
        ...buildParams(),
        page: pagination.currentPage,
        limit: pagination.pageSize,
      });
      setSummary(data.summary || EMPTY_SUMMARY);
      setRows(data.rows || []);
      setPagination((prev) => ({ ...prev, total: data.total || 0 }));
    } catch (err) {
      console.error('Failed to load product wise sales:', err);
      showError(err.response?.data?.message || t('report.productWiseSales.failedToLoad'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.shopIds,
    filters.channels,
    filters.date?.dateFrom,
    filters.date?.dateTo,
    filters.search,
    sorting.orderBy,
    sorting.orderDirection,
    pagination.currentPage,
    pagination.pageSize,
  ]);

  const resetPage = () => setPagination((prev) => ({ ...prev, currentPage: 1 }));

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    resetPage();
  };

  const handleClearFilters = () => {
    setFilters(getDefaultFilters());
    resetPage();
  };

  const handleSortChange = (newSorting) => {
    setSorting(newSorting);
    resetPage();
  };

  const shopLabel = useMemo(() => {
    if (filters.shopIds.length === 0) return t('report.productWiseSales.allShops');
    return shops.filter((s) => filters.shopIds.includes(s.id)).map((s) => s.name).join(', ');
  }, [filters.shopIds, shops, t]);

  const channelLabel = filters.channels.map((c) => t(CHANNEL_LABEL_KEYS[c])).join(', ');

  const handleChannelToggle = (channel, value, onChange) => {
    const current = value || [];
    if (current.includes(channel) && current.length === 1) return;
    onChange(current.includes(channel) ? current.filter((c) => c !== channel) : [...current, channel]);
  };

  const handleExport = async (type) => {
    if (exporting) return;
    if (filters.channels.length === 0) {
      showWarning(t('report.selectAtLeastOneChannel'));
      return;
    }
    try {
      setExporting(type);
      const data = await getProductWiseSalesReport({ ...buildParams(), all: true });
      const payload = {
        rows: data.rows || [],
        summary: data.summary || EMPTY_SUMMARY,
        meta: { dateFrom: filters.date.dateFrom, dateTo: filters.date.dateTo, shopLabel, channelLabel },
      };
      if (type === 'excel') {
        await exportProductWiseSalesExcel(payload);
      } else {
        exportProductWiseSalesPDF(payload, {
          primary: getCssVar('--color-primary', '#4f46e5'),
          primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
        });
      }
    } catch (err) {
      console.error('Product wise sales export error:', err);
      showError(t('report.productWiseSales.exportFailed'));
    } finally {
      setExporting(null);
    }
  };

  const filterDefs = [
    {
      key: 'shopIds',
      label: t('report.productWiseSales.shops'),
      type: 'multiselect',
      placeholder: t('report.productWiseSales.allShops'),
      options: shops,
      displayKey: 'name',
      valueKey: 'id',
      searchKey: 'name',
    },
    {
      key: 'channels',
      label: t('report.channel'),
      type: 'custom',
      render: (value, onChange) => (
        <div className={styles.channelFilters}>
          {REPORT_CHANNELS.map((channel) => {
            const checked = (value || []).includes(channel);
            const locked = checked && (value || []).length === 1;
            return (
            <label key={channel} className={`${styles.checkboxLabel} ${locked ? styles.checkboxLabelDisabled : ''}`}>
              <input
                type="checkbox"
                checked={checked}
                disabled={locked}
                onChange={() => handleChannelToggle(channel, value, onChange)}
                className={styles.checkbox}
              />
              <span>{t(CHANNEL_LABEL_KEYS[channel])}</span>
            </label>
            );
          })}
        </div>
      ),
    },
    {
      key: 'date',
      label: t('report.productWiseSales.dateRange'),
      type: 'custom',
      gridColumn: '1 / -1',
      render: (value, onChange) => <ReportDatePresetFilter value={value} onChange={onChange} />,
    },
  ];

  const cardLabel = (key) => t(`report.productWiseSales.cards.${key}`);
  const stats = [
    { key: 'orderCount', label: cardLabel('orderCount'), value: summary.orderCount, icon: ShoppingCart, color: '#6366f1', format: (v) => Number(v).toLocaleString() },
    { key: 'orderAmount', label: cardLabel('orderAmount'), value: summary.orderAmount, icon: DollarSign, color: '#10b981', format: (v) => formatCurrency(v, 'LKR') },
    { key: 'orderDiscount', label: cardLabel('orderDiscount'), value: summary.orderDiscount, icon: Tag, color: '#f59e0b', format: (v) => formatCurrency(v, 'LKR') },
    { key: 'productCount', label: cardLabel('productCount'), value: summary.productCount, icon: Boxes, color: '#3b82f6', format: (v) => Number(v).toLocaleString() },
    { key: 'productAmount', label: cardLabel('productAmount'), value: summary.productAmount, icon: DollarSign, color: '#14b8a6', format: (v) => formatCurrency(v, 'LKR') },
    { key: 'productDiscount', label: cardLabel('productDiscount'), value: summary.productDiscount, icon: Tag, color: '#ef4444', format: (v) => formatCurrency(v, 'LKR') },
  ];

  const colHeader = (key) => t(`report.productWiseSales.columns.${key}`);
  const numericColumn = (key, render, width = '140px') => ({
    key,
    header: colHeader(key),
    sortable: true,
    align: 'right',
    width,
    render: (row) => <span className={styles.numberCell}>{render(row[key])}</span>,
  });

  const columns = [
    {
      key: 'productName',
      header: colHeader('productName'),
      sortable: true,
      width: '260px',
      render: (row) => (
        <div className={styles.productCell}>
          <Package size={14} />
          <span>{row.productName}</span>
        </div>
      ),
    },
    numericColumn('itemCount', (v) => Number(v || 0).toLocaleString(), '110px'),
    numericColumn('amount', fmtNumber),
    numericColumn('contribution', fmtPercent, '130px'),
    numericColumn('discount', fmtNumber),
    numericColumn('amountAfterDiscount', fmtNumber, '170px'),
    numericColumn('amountPerItem', fmtNumber),
    numericColumn('discountPerItem', fmtNumber),
    numericColumn('discountPctPerItem', fmtPercent, '150px'),
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('report.productWiseSales.title')}
        description={t('report.productWiseSales.description')}
        icon={Package}
        actions={
          <div className={styles.headerActions}>
            <button onClick={() => handleExport('excel')} className={styles.downloadExcelButton} disabled={!!exporting}>
              <Sheet size={16} />
              <span>{exporting === 'excel' ? t('common.processing') : t('report.productWiseSales.excel')}</span>
            </button>
            <button onClick={() => handleExport('pdf')} className={styles.downloadPdfButton} disabled={!!exporting}>
              <Download size={16} />
              <span>{exporting === 'pdf' ? t('common.processing') : t('report.productWiseSales.pdf')}</span>
            </button>
          </div>
        }
      />

      <FilterPanel
        filters={filterDefs}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters((s) => !s)}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: (value) => handleFilterChange({ search: value }),
          placeholder: t('report.productWiseSales.searchPlaceholder'),
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: (size) => setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 })),
          options: pagination.pageSizeOptions,
        }}
      />

      <StatsCards loading={isLoading} stats={stats} columns={3} valueFontSize="1.375rem" />

      <DataTable
        data={rows.map((row) => ({ ...row, id: `${row.productId}-${row.variationId ?? 0}` }))}
        columns={columns}
        sorting={sorting}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={t('report.productWiseSales.noData')}
        emptyIcon={<Package size={64} />}
      />
    </div>
  );
};

export default ProductWiseSalesPage;
